import styled from '@emotion/styled'
import { SerializedStyles, css } from '@emotion/react'
import { ImageField } from '@prismicio/client'
import { ScrollOverMediaDocumentDataBodyImageParallaxStepSlicePrimary } from '../../../../../types.generated'
import { useEffect, useState } from 'react'
import { useIsPortrait } from '../../../../util/breakpoints'
import { LazyImage } from '../../../atoms/LazyImage'
import isPropValid from '@emotion/is-prop-valid'
import { VIEWPORT_HEIGHT } from '../../../../util/viewportHeight'

type ImageLandscapeType =
  ScrollOverMediaDocumentDataBodyImageParallaxStepSlicePrimary['image_landscape']
type ImagePortraitType =
  ScrollOverMediaDocumentDataBodyImageParallaxStepSlicePrimary['image_landscape']

const StyledImage = styled(LazyImage, {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ effect?: SerializedStyles }>`
  object-fit: cover;
  height: ${VIEWPORT_HEIGHT};
  width: 100%;

  ${(props) => props.effect || ''}
`

interface ParallaxImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  imageLandscape: ImageLandscapeType
  imagePortrait: ImagePortraitType
  scrollPercentage: number
}

const decideImage = (
  imageLandscape: ImageLandscapeType,
  imagePortrait: ImagePortraitType,
  isPortrait: boolean
): ImageField => {
  if (isPortrait && imagePortrait?.url) {
    return imagePortrait
  } else {
    return imageLandscape
  }
}

export const ParallaxImage = (props: ParallaxImageProps) => {
  const [parallaxEffect, setParallaxEffect] = useState<
    SerializedStyles | undefined
  >(undefined)

  const isPortrait = useIsPortrait()
  const image = decideImage(
    props.imageLandscape,
    props.imagePortrait,
    isPortrait
  )

  useEffect(() => {
    if (!image.dimensions) {
      console.error(
        `Image dimensions of ${image.url} are missing. Ignoring parallax effect...`
      )
      return
    }

    // Find out the height of our rendered parallax image using the image dimensions and the window width.
    const imageScaleFactor = window.innerWidth / image.dimensions.width
    const renderedImageHeight = image.dimensions.height * imageScaleFactor

    // Position where rendered image will leave the bottom of the screen
    const maxScrollPosition = renderedImageHeight - window.innerHeight

    const currentScrollPosition = maxScrollPosition * props.scrollPercentage

    setParallaxEffect(css`
      width: 100%;
      height: auto;
      transform: translateY(-${currentScrollPosition}px);
    `)
  }, [image, props.scrollPercentage])

  if (!image.dimensions?.width || !image.dimensions.height) {
    return <StyledImage src={image.url ?? ''} alt={image.alt ?? ''} />
  }

  return (
    <StyledImage
      src={image.url ?? ''}
      alt={image.alt ?? ''}
      effect={parallaxEffect ?? css``}
      {...props}
    />
  )
}
